import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, Layers, GitBranch, Settings, ArrowRight, CheckCircle, 
  Play, Star, TrendingUp, FileText, Link, Zap, ChevronRight, Code,
  Search, Filter, Download, Upload, RefreshCw, Lock, Users, Clock
} from 'lucide-react';

const DataManagement = () => {
  const [activeFeature, setActiveFeature] = useState('crud');

  const dataFeatures = [
    {
      id: 'crud',
      name: 'Object & Record Management',
      icon: Database,
      description: 'Complete lifecycle management of business objects and records',
      capabilities: [
        'Create, read, update, delete operations on any data object',
        'Bulk data processing for large-scale operations',
        'Data validation and integrity enforcement',
        'Record relationships and complex lookups',
        'Paginated data retrieval for performance',
        'Advanced search and filtering capabilities'
      ],
      apiEndpoints: [
        { name: 'getObjectListData', description: 'Retrieve paginated object data' },
        { name: 'saveObjectRecord', description: 'Create new records' },
        { name: 'getRecordDetails', description: 'Get detailed record view' },
        { name: 'saveRecord', description: 'Update existing records' },
        { name: 'deleteRecord', description: 'Remove records safely' },
        { name: 'getObjectRecord', description: 'Lookup related records' }
      ],
      useCases: [
        'Customer relationship management with complete interaction history',
        'Product catalog management with variants and pricing',
        'Employee records with performance tracking and reviews',
        'Project management with tasks, milestones, and dependencies'
      ]
    },
    {
      id: 'relationships',
      name: 'Advanced Data Operations',
      icon: GitBranch,
      description: 'Sophisticated data manipulation and relationship management',
      capabilities: [
        'Complex queries with multiple conditions and joins',
        'Master-detail relationships and hierarchical data',
        'Custom field types with validation rules',
        'Data transformation and mapping capabilities',
        'Cross-object reporting and analytics',
        'Real-time data synchronization'
      ],
      apiEndpoints: [
        { name: 'getObjectRecordType', description: 'Get object schema information' },
        { name: 'getObjectRecordCreationData', description: 'Get creation templates' },
        { name: 'getPageDetails', description: 'Get page layout configurations' },
        { name: 'invokeFunction', description: 'Execute custom data operations' }
      ],
      useCases: [
        'Multi-level approval workflows with escalation paths',
        'Inventory management with supplier and location relationships',
        'Financial reporting with account hierarchies and consolidation',
        'Project tracking with resource allocation and timeline dependencies'
      ]
    },
    {
      id: 'metadata',
      name: 'Metadata Management',
      icon: Settings,
      description: 'Dynamic schema management and object customization',
      capabilities: [
        'Dynamic object creation and modification',
        'Custom field definitions with validation',
        'Schema evolution and version management',
        'Object relationship mapping and constraints',
        'Data model documentation and governance',
        'Import/export of schema configurations'
      ],
      apiEndpoints: [
        { name: 'getOrgObjects', description: 'List all organization objects' },
        { name: 'getObjectAndFieldDetails', description: 'Get complete object metadata' },
        { name: 'getAccessibleObjects', description: 'Get user-accessible objects' }
      ],
      useCases: [
        'Rapid adaptation to changing business requirements',
        'Industry-specific data models and compliance structures',
        'Multi-tenant applications with customizable schemas',
        'Data governance and regulatory compliance management'
      ]
    }
  ];

  const fieldTypes = [
    { name: 'Text', description: 'Single line text input', icon: '📝' },
    { name: 'Number', description: 'Numeric values with validation', icon: '🔢' },
    { name: 'Email', description: 'Email address with validation', icon: '📧' },
    { name: 'Date/DateTime', description: 'Date and time pickers', icon: '📅' },
    { name: 'Boolean', description: 'True/false checkboxes', icon: '☑️' },
    { name: 'Currency', description: 'Monetary values with formatting', icon: '💰' },
    { name: 'Picklist', description: 'Single and multi-select dropdowns', icon: '📋' },
    { name: 'Lookup', description: 'References to other objects', icon: '🔗' },
    { name: 'Rich Text', description: 'Formatted text editor', icon: '📄' },
    { name: 'File/Image', description: 'File uploads and attachments', icon: '📎' },
    { name: 'Location', description: 'Geographic coordinates', icon: '📍' },
    { name: 'Phone', description: 'Phone number with formatting', icon: '📞' }
  ];

  const dataOperations = [
    {
      name: 'Bulk Import/Export',
      icon: Upload,
      description: 'Handle large datasets efficiently',
      features: ['CSV/Excel import', 'Data validation', 'Error reporting', 'Batch processing']
    },
    {
      name: 'Advanced Search',
      icon: Search,
      description: 'Powerful search across all data',
      features: ['Full-text search', 'Faceted filtering', 'Saved searches', 'Global search']
    },
    {
      name: 'Data Validation',
      icon: CheckCircle,
      description: 'Ensure data quality and integrity',
      features: ['Custom validation rules', 'Required field enforcement', 'Format validation', 'Business rule validation']
    },
    {
      name: 'Real-time Sync',
      icon: RefreshCw,
      description: 'Keep data synchronized across systems',
      features: ['Change tracking', 'Conflict resolution', 'Incremental sync', 'Event-driven updates']
    }
  ];

  const industryExamples = [
    {
      industry: 'Healthcare',
      icon: '🏥',
      objects: ['Patient Records', 'Medical History', 'Appointments', 'Insurance Claims'],
      relationships: 'Patients → Medical History → Treatments → Billing',
      complexity: 'HIPAA compliance with audit trails'
    },
    {
      industry: 'Manufacturing',
      icon: '🏭',
      objects: ['Products', 'Bill of Materials', 'Inventory', 'Quality Control'],
      relationships: 'Products → BOM → Components → Suppliers',
      complexity: 'Multi-level BOMs with version control'
    },
    {
      industry: 'Financial Services',
      icon: '🏦',
      objects: ['Accounts', 'Transactions', 'Customers', 'Compliance Reports'],
      relationships: 'Customers → Accounts → Transactions → Reporting',
      complexity: 'Regulatory compliance with data retention'
    },
    {
      industry: 'Education',
      icon: '🎓',
      objects: ['Students', 'Courses', 'Grades', 'Transcripts'],
      relationships: 'Students → Enrollments → Courses → Grades',
      complexity: 'Academic year cycles with historical data'
    }
  ];

  const testimonials = [
    {
      quote: "The flexible data model allowed us to adapt quickly to new regulations without rebuilding our entire system.",
      name: "",
      title: "",
      company: "FinanceFirst",
      avatar: "👩‍💼",
      metric: "90% faster compliance"
    },
    {
      quote: "Managing complex product hierarchies became simple with Aquargin's relationship management capabilities.",
      name: "",
      title: "",
      company: "Global Manufacturing",
      avatar: "👨‍💼",
      metric: "50% reduction in data errors"
    },
    {
      quote: "The bulk data operations saved us weeks of manual work during our system migration.",
      name: "",
      title: "",
      company: "Healthcare Systems",
      avatar: "👩‍💻",
      metric: "2M records migrated"
    }
  ];

  const stats = [
    { number: '10M+', label: 'Records Processed Daily', description: 'Across all client systems' },
    { number: '99.99%', label: 'Data Integrity', description: 'With built-in validation' },
    { number: '<100ms', label: 'Query Response Time', description: 'For most operations' },
    { number: '50+', label: 'Field Types', description: 'Supported out of the box' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left text-white"
            >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white mb-6">
              <Database className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Data Management
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-blue-200">
                Complete Data Lifecycle Control
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Powerful data management capabilities that handle everything from simple CRUD operations 
              to complex relationships and dynamic schema evolution.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try Data Features Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Data Demo
              </motion.a>
            </div>
          </motion.div>
          
          {/* Data Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <img
              src="/images/3D_isometric_view_of_structured_data_cubes_floatin_1750595537.png"
              alt="Data Architecture Visualization"
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Features Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center">
              <img
                src="/images/Streams_of_flowing_data_particles_moving_through_t_1750595569.png"
                alt="Data Flow Processing"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Data Processing</h3>
              <p className="text-gray-600 mt-2">Real-time data transformation and processing</p>
            </div>
            <div className="text-center">
              <img
                src="/images/Professional_interface_showing_bulk_data_operation_1750596752.png"
                alt="Data Import Export"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">Bulk Operations</h3>
              <p className="text-gray-600 mt-2">Large-scale data import and export capabilities</p>
            </div>
            <div className="text-center">
              <img
                src="/images/list-view.png"
                alt="Data Management Interface"
                className="w-full h-48 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900">List Management</h3>
              <p className="text-gray-600 mt-2">Intuitive data browsing and management interface</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Data Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Core Data Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive data management from basic operations to advanced relationships
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {dataFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setActiveFeature(feature.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeFeature === feature.id
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <feature.icon className="h-5 w-5" />
                <span>{feature.name}</span>
              </button>
            ))}
          </div>

          {/* Active Feature Content */}
          {dataFeatures.map((feature) => (
            activeFeature === feature.id && (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{feature.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{feature.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Capabilities
                      </h4>
                      <ul className="space-y-3">
                        {feature.capabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Code className="h-5 w-5 text-blue-500 mr-2" />
                        API Endpoints
                      </h4>
                      <div className="space-y-3">
                        {feature.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                            <div className="font-mono text-sm text-blue-600 font-semibold">
                              {endpoint.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {endpoint.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="h-5 w-5 text-blue-500 mr-2" />
                        Use Cases
                      </h4>
                      <div className="space-y-3">
                        {feature.useCases.map((useCase, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <ChevronRight className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700">{useCase}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Field Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Supported Field Types
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rich data types to model any business requirement
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {fieldTypes.map((fieldType, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-3xl mb-3">{fieldType.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{fieldType.name}</h3>
                <p className="text-sm text-gray-600">{fieldType.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Operations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Advanced Data Operations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful tools for managing data at scale
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {dataOperations.map((operation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4">
                    <operation.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{operation.name}</h3>
                    <p className="text-gray-600">{operation.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {operation.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Examples */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry Data Models
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how different industries structure their data with Aquargin
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {industryExamples.map((example, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{example.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{example.industry}</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Key Objects:</h4>
                    <div className="flex flex-wrap gap-2">
                      {example.objects.map((object, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                          {object}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Relationships:</h4>
                    <p className="text-gray-600 font-mono text-sm bg-gray-100 p-2 rounded">
                      {example.relationships}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Complexity:</h4>
                    <p className="text-gray-600">{example.complexity}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Data Management Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from organizations using Aquargin's data capabilities
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Result</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Master Your Data Management
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Start your free trial and experience the power of comprehensive 
              data management with Aquargin's flexible platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Data Trial Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Database className="mr-2 h-5 w-5" />
                Schedule Data Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full data access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DataManagement; 